import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, LinearProgress, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import type { IContest } from '../../../types/global/helper';
import { getContestFundingRequirements } from '../../../redux/contest/actions';
import type { IContestFundingRequirements } from '../../../redux/contest/types';
import { formatDate, formatNumberWithK, useDispatchPromise } from '../../../utils/AppUtils';
import { formatUsdFromCents } from '../contestWizard/fundingUtils';
import { RichTextViewer } from '../../../components/Common/RichTextViewer';
import { RectangularImageCropperModal } from '../../../components/ImageCropper/RectangularImageCropperModal';
import { RICH_MEDIA_SHARE_ASPECT } from '../../../constants/richMediaShareImage';
import { RichMediaShareSizeHint } from '../../../components/Common/RichMediaShareSizeHint';
import {
    brandContestRichTextViewerSx,
    editorialHeadlineSx,
    editorialLabelSx,
    midnight,
} from '../contestWizard/midnightStyles';

export type ContestLifecyclePhase = 'draft' | 'funded' | 'live';

export function getContestLifecyclePhase(contest: IContest): ContestLifecyclePhase {
    if (contest.status === 'published') return 'live';
    if (contest.funded) return 'funded';
    return 'draft';
}

function statusPillLabel(contest: IContest): string {
    if (contest.status === 'published') return 'Live';
    if (!contest.funded) return 'Awaiting funding';
    return 'Funded — ready to publish';
}

const guidelineIcons = [VideocamOutlinedIcon, MusicNoteOutlinedIcon];

function formatSpecLabel(raw: string | undefined): string {
    if (!raw) return '—';
    return raw
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function EditorialListRow({
    label,
    value,
    Icon,
    toneIndex,
}: {
    label: string;
    value?: string;
    Icon: SvgIconComponent;
    toneIndex: number;
}) {
    const iconBg = toneIndex % 2 === 0 ? 'rgba(255,201,101,0.1)' : 'rgba(0,253,193,0.1)';
    const iconColor = toneIndex % 2 === 0 ? midnight.primary : midnight.secondary;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                bgcolor: midnight.surfaceContainer,
                borderRadius: '8px',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: midnight.surfaceHighest },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '4px',
                        bgcolor: iconBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <Icon sx={{ color: iconColor }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: midnight.onSurface }}>{label}</Typography>
                    {value ? (
                        <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant, mt: 0.25 }}>
                            {value}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            <ChevronRightIcon sx={{ color: midnight.onSurfaceVariant, flexShrink: 0 }} />
        </Box>
    );
}

function EditorialSpecTile({
    label,
    value,
    Icon,
    toneIndex,
}: {
    label: string;
    value: string;
    Icon: SvgIconComponent;
    toneIndex: number;
}) {
    const iconBg = toneIndex % 2 === 0 ? 'rgba(255,201,101,0.1)' : 'rgba(0,253,193,0.1)';
    const iconColor = toneIndex % 2 === 0 ? midnight.primary : midnight.secondary;

    return (
        <Box
            sx={{
                flex: { xs: '1 1 auto', md: '1 1 0' },
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 0 },
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: midnight.surfaceContainer,
                borderRadius: '8px',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: midnight.surfaceHighest },
            }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '4px',
                    bgcolor: iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                <Icon sx={{ color: iconColor }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: midnight.onSurfaceVariant,
                        mb: 0.25,
                    }}>
                    {label}
                </Typography>
                <Typography sx={{ fontWeight: 700, color: midnight.onSurface, fontSize: 15 }}>{value}</Typography>
            </Box>
        </Box>
    );
}

interface Props {
    contest: IContest;
    submissionCount: number;
    salesFormatted: string;
    voteCount: number;
    onFundClick: () => void;
    onPublishClick?: () => void;
    showFundButton?: boolean;
    showPublishButton?: boolean;
    hideFinances?: boolean;
    /** Grid + drawers for full manage workspaces (brand route). */
    manageHub?: React.ReactNode;
    /** Upload cropped feature image (same payload as legacy ContestCoverImage). */
    onFeatureImageChange?: (previewUrl: string, file: File) => void;
    /** Bumped after funding dialog closes so escrow amount refetches from the API. */
    fundingRefreshKey?: number;
}

export function BrandContestEditorialView({
    contest,
    submissionCount,
    salesFormatted,
    voteCount,
    onFundClick,
    onPublishClick,
    showFundButton = true,
    showPublishButton = false,
    hideFinances = false,
    manageHub,
    onFeatureImageChange,
    fundingRefreshKey = 0,
}: Props) {
    const dispatchPromise = useDispatchPromise();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropSrc, setCropSrc] = useState('');
    const [localHeroPreview, setLocalHeroPreview] = useState<string | null>(null);
    const [fundingRequirements, setFundingRequirements] = useState<IContestFundingRequirements | null>(null);

    const loadFundingRequirements = useCallback(async () => {
        if (!contest._id) return;
        try {
            const data = (await dispatchPromise(
                getContestFundingRequirements(contest._id),
            )) as IContestFundingRequirements;
            setFundingRequirements(data);
        } catch {
            setFundingRequirements(null);
        }
    }, [contest._id, dispatchPromise]);

    useEffect(() => {
        void loadFundingRequirements();
    }, [loadFundingRequirements, contest.funded, contest.amount_available, fundingRefreshKey]);

    const phase = getContestLifecyclePhase(contest);
    const heroImage = contest.feature_image || contest.mobile_feature_image;
    const displayHeroImage = localHeroPreview || heroImage;

    const prevHeroImageRef = useRef(heroImage);
    useEffect(() => {
        if (prevHeroImageRef.current !== heroImage && localHeroPreview?.startsWith('blob:')) {
            URL.revokeObjectURL(localHeroPreview);
            setLocalHeroPreview(null);
        }
        prevHeroImageRef.current = heroImage;
    }, [heroImage, localHeroPreview]);

    useEffect(() => {
        return () => {
            if (localHeroPreview?.startsWith('blob:')) {
                URL.revokeObjectURL(localHeroPreview);
            }
        };
    }, [localHeroPreview]);

    const handleHeroUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleHeroFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setCropSrc(URL.createObjectURL(file));
        setCropModalOpen(true);
        event.target.value = '';
    };

    const handleHeroCropSave = (_base64: string, file: File) => {
        setCropModalOpen(false);
        if (cropSrc.startsWith('blob:')) {
            URL.revokeObjectURL(cropSrc);
        }
        setCropSrc('');
        const preview = URL.createObjectURL(file);
        setLocalHeroPreview(prev => {
            if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
            return preview;
        });
        onFeatureImageChange?.(preview, file);
    };

    const handleHeroCropCancel = () => {
        setCropModalOpen(false);
        if (cropSrc.startsWith('blob:')) {
            URL.revokeObjectURL(cropSrc);
        }
        setCropSrc('');
    };
    const escrowCents = fundingRequirements?.funded_cents ?? 0;
    const prizePoolCents = fundingRequirements?.required_total_cents ?? 0;
    const escrowDisplay = hideFinances
        ? 'xxxxx'
        : fundingRequirements
        ? formatUsdFromCents(escrowCents)
        : contest.funded
        ? '…'
        : '$0.00';
    const prizePoolDisplay = hideFinances ? 'xxxxx' : prizePoolCents > 0 ? formatUsdFromCents(prizePoolCents) : null;

    const phases: { key: ContestLifecyclePhase; label: string; title: string; Icon: typeof EditNoteOutlinedIcon }[] = [
        { key: 'draft', label: 'Phase 01', title: 'Draft content', Icon: EditNoteOutlinedIcon },
        { key: 'funded', label: 'Phase 02', title: 'Funded & locked', Icon: AccountBalanceWalletOutlinedIcon },
        { key: 'live', label: 'Phase 03', title: 'Go live', Icon: RocketLaunchOutlinedIcon },
    ];
    const phaseIndex = phases.findIndex(p => p.key === phase);

    const requirements = (contest.requirements || [])
        .map(r => (typeof r === 'string' ? r : r.header || r.detail || ''))
        .filter(Boolean);
    const guidelines =
        requirements.length > 0 ? requirements : (contest.tag || []).slice(0, 4).map(t => `Content tagged: ${t}`);

    const genres = (contest.genre || []).join(', ');
    const tags = (contest.tag || []).join(', ');

    const technicalSpecRows = useMemo(() => {
        const spec = contest.technical_spec;
        const minDuration = spec?.min_duration ?? 1;
        const maxDuration = spec?.max_duration ?? 2;
        const mediaType = spec?.media_type ?? 'video';
        const projectFormat = spec?.project_format ?? spec?.format ?? 'video';

        return [
            {
                label: 'Min duration',
                value: `${minDuration} minute${minDuration === 1 ? '' : 's'}`,
                Icon: TimerOutlinedIcon,
            },
            {
                label: 'Max duration',
                value: `${maxDuration} minute${maxDuration === 1 ? '' : 's'}`,
                Icon: TimerOutlinedIcon,
            },
            {
                label: 'Media type',
                value: formatSpecLabel(mediaType),
                Icon: MovieOutlinedIcon,
            },
            {
                label: 'Project format',
                value: formatSpecLabel(String(projectFormat)),
                Icon: TheatersOutlinedIcon,
            },
        ];
    }, [contest.technical_spec]);

    const milestones = [
        {
            done: true,
            active: false,
            title: 'Conceptual approval',
            subtitle: contest.date_start
                ? `Draft saved · starts ${formatDate(contest.date_start, 'MMM D, YY')}`
                : 'Campaign created',
            Icon: CheckCircleIcon,
            iconColor: midnight.secondary,
        },
        {
            done: contest.funded,
            active: !contest.funded && phase === 'draft',
            title: 'Capital raising',
            subtitle: contest.funded
                ? `Escrow locked · ${escrowDisplay}`
                : 'Fund your prize pool from the campaign wallet',
            Icon: contest.funded ? CheckCircleIcon : RadioButtonCheckedIcon,
            iconColor: midnight.primary,
        },
        {
            done: contest.status === 'published',
            active: contest.funded && contest.status !== 'published',
            title: 'Creator activation',
            subtitle: contest.status === 'published' ? 'Campaign is live' : 'Unlocks when you publish',
            Icon: contest.status === 'published' ? CheckCircleIcon : CircleOutlinedIcon,
            iconColor: midnight.onSurfaceVariant,
            muted: contest.status !== 'published' && !contest.funded,
        },
    ];

    const fundingPct =
        fundingRequirements && fundingRequirements.required_total_cents > 0
            ? Math.min(
                  100,
                  Math.round((fundingRequirements.funded_cents / fundingRequirements.required_total_cents) * 100),
              )
            : contest.funded
            ? 100
            : 0;

    return (
        <Box sx={{ width: '100%', bgcolor: midnight.surface, color: midnight.onSurface, pb: 10 }}>
            <Box sx={{ position: 'relative', width: '100%', height: { xs: 360, md: 450 }, overflow: 'hidden' }}>
                {displayHeroImage ? (
                    <Box
                        component="img"
                        src={displayHeroImage}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            background: `linear-gradient(135deg, ${midnight.surfaceContainer} 0%, ${midnight.surface} 100%)`,
                        }}
                    />
                )}
                {onFeatureImageChange && (
                    <>
                        <IconButton
                            onClick={handleHeroUploadClick}
                            aria-label="Change cover image"
                            sx={{
                                position: 'absolute',
                                top: { xs: 16, md: 20 },
                                right: { xs: 16, md: 20 },
                                zIndex: 3,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: midnight.onSurface,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)', color: midnight.primary },
                            }}>
                            <PhotoCameraOutlinedIcon />
                        </IconButton>
                        <RichMediaShareSizeHint
                            sx={{
                                position: 'absolute',
                                top: { xs: 64, md: 68 },
                                right: { xs: 16, md: 20 },
                                zIndex: 3,
                                color: midnight.onSurface,
                                opacity: 0.75,
                                fontSize: 10,
                                mt: 0,
                                textAlign: 'right',
                                maxWidth: 140,
                                lineHeight: 1.3,
                            }}
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleHeroFileChange}
                        />
                        <RectangularImageCropperModal
                            open={cropModalOpen}
                            src={cropSrc}
                            onSave={handleHeroCropSave}
                            onCancel={handleHeroCropCancel}
                            aspect={RICH_MEDIA_SHARE_ASPECT}
                            cropHeight={200}
                            variant="richMediaShare"
                        />
                    </>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(to top, ${midnight.surface} 0%, rgba(14,14,14,0.4) 50%, transparent 100%)`,
                        zIndex: 1,
                    }}
                />
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 5 }, zIndex: 2 }}>
                    <Box
                        sx={{
                            maxWidth: 1280,
                            mx: 'auto',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { md: 'flex-end' },
                            justifyContent: 'space-between',
                            gap: 3,
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.5,
                                    mb: 2,
                                    bgcolor: 'rgba(255,201,101,0.1)',
                                    border: '1px solid rgba(255,201,101,0.2)',
                                    borderRadius: 999,
                                }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: midnight.primary,
                                        animation: contest.funded ? 'none' : 'pulse 2s infinite',
                                        '@keyframes pulse': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0.4 },
                                        },
                                    }}
                                />
                                <Typography sx={{ ...editorialLabelSx, color: midnight.primary, mb: 0 }}>
                                    {statusPillLabel(contest)}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    ...editorialHeadlineSx,
                                    fontSize: { xs: 36, md: 56 },
                                    color: midnight.onSurface,
                                    lineHeight: 1.05,
                                    letterSpacing: '-0.04em',
                                }}>
                                {contest.title}
                            </Typography>
                            {contest.description?.trim() ? (
                                <Box
                                    sx={{
                                        mt: 2,
                                        maxWidth: 640,
                                        fontSize: { xs: 16, md: 18 },
                                        color: midnight.onSurfaceVariant,
                                        ...brandContestRichTextViewerSx,
                                        '& .rich-text-wrapper .ql-editor': {
                                            color: `${midnight.onSurfaceVariant} !important`,
                                            padding: 0,
                                        },
                                    }}>
                                    <RichTextViewer
                                        content={contest.description}
                                        lineClamp={3}
                                        textColor={midnight.onSurfaceVariant}
                                        textStyle={{
                                            padding: 0,
                                            fontSize: 'inherit',
                                            lineHeight: 1.5,
                                        }}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                alignSelf: { md: 'flex-start' },
                            }}>
                            {showFundButton && (
                                <Button
                                    onClick={onFundClick}
                                    sx={{
                                        px: 5,
                                        py: 2,
                                        borderRadius: 999,
                                        fontWeight: 800,
                                        fontSize: 18,
                                        color: '#392700',
                                        background: `linear-gradient(90deg, ${midnight.primary}, #feb700)`,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                                        '&:hover': {
                                            background: `linear-gradient(90deg, #feb700, ${midnight.primary})`,
                                            filter: 'brightness(1.05)',
                                        },
                                    }}>
                                    {contest.funded ? 'Add more funds' : 'Fund campaign'}
                                </Button>
                            )}
                            {showPublishButton && onPublishClick && (
                                <Button
                                    onClick={onPublishClick}
                                    sx={{
                                        px: 5,
                                        py: 2,
                                        borderRadius: 999,
                                        fontWeight: 800,
                                        fontSize: 16,
                                        color: midnight.onSurface,
                                        border: `1px solid ${midnight.secondary}`,
                                        '&:hover': { bgcolor: 'rgba(0,253,193,0.08)' },
                                    }}>
                                    Publish campaign
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 3 }, mt: 6 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 0.125,
                        mb: 6,
                    }}>
                    {phases.map((p, idx) => {
                        const isActive = idx === phaseIndex;
                        const isPast = idx < phaseIndex;
                        const Icon = p.Icon;
                        return (
                            <Box
                                key={p.key}
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    p: 3,
                                    bgcolor: isActive ? midnight.surfaceContainer : midnight.surfaceLow,
                                    borderRadius:
                                        idx === 0
                                            ? { md: '12px 0 0 12px', xs: '12px 12px 0 0' }
                                            : idx === 2
                                            ? { md: '0 12px 12px 0', xs: '0 0 12px 12px' }
                                            : 0,
                                    borderLeft: idx === 1 ? { md: '1px solid rgba(14,14,14,0.5)' } : undefined,
                                    borderRight: idx === 1 ? { md: '1px solid rgba(14,14,14,0.5)' } : undefined,
                                }}>
                                <Box sx={{ position: 'relative', zIndex: 1 }}>
                                    <Typography
                                        sx={{
                                            ...editorialLabelSx,
                                            letterSpacing: '0.2em',
                                            color: isActive ? midnight.primary : midnight.onSurfaceVariant,
                                            fontWeight: isActive ? 700 : 600,
                                        }}>
                                        {p.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...editorialHeadlineSx,
                                            fontSize: 20,
                                            mt: 0.5,
                                            textTransform: 'capitalize',
                                            color: isActive
                                                ? midnight.primary
                                                : isPast
                                                ? midnight.onSurface
                                                : 'rgba(255,255,255,0.2)',
                                        }}>
                                        {p.title}
                                    </Typography>
                                </Box>
                                {isActive && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            height: 4,
                                            width: '66%',
                                            bgcolor: midnight.primary,
                                        }}
                                    />
                                )}
                                <Icon
                                    sx={{
                                        position: 'absolute',
                                        right: -10,
                                        bottom: -10,
                                        fontSize: 96,
                                        opacity: isActive ? 0.1 : 0.05,
                                        color: isActive ? midnight.primary : midnight.onSurface,
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                        gap: 3,
                        mb: 6,
                        alignItems: 'stretch',
                    }}>
                    <Box
                        sx={{
                            gridColumn: { xs: 'span 1', md: 'span 2' },
                            bgcolor: midnight.surfaceContainer,
                            borderRadius: '12px',
                            p: 4,
                            minHeight: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}>
                        <Box
                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                            <Box>
                                <Typography sx={{ ...editorialLabelSx, fontSize: 12, mb: 0.5 }}>
                                    Total submissions
                                </Typography>
                                <Typography sx={{ ...editorialHeadlineSx, fontSize: 48, color: midnight.onSurface }}>
                                    {formatNumberWithK(submissionCount)}
                                </Typography>
                            </Box>
                            {submissionCount > 0 && (
                                <Box sx={{ bgcolor: 'rgba(0,253,193,0.1)', px: 1.5, py: 0.5, borderRadius: 999 }}>
                                    <Typography sx={{ color: midnight.secondary, fontWeight: 700, fontSize: 12 }}>
                                        Active
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, submissionCount > 0 ? 72 : 8)}
                            sx={{
                                mt: 4,
                                height: 4,
                                borderRadius: 999,
                                bgcolor: midnight.surfaceHighest,
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: midnight.secondary,
                                    boxShadow: '0 0 8px rgba(0,253,193,0.4)',
                                },
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            gridColumn: { xs: 'span 1', md: 'span 1' },
                            bgcolor: midnight.surfaceContainer,
                            borderRadius: '12px',
                            p: 4,
                            minHeight: { md: 200 },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}>
                        <Box>
                            <Typography sx={{ ...editorialLabelSx, fontSize: 12, mb: 0.5 }}>Pre-sales</Typography>
                            <Typography
                                sx={{
                                    ...editorialHeadlineSx,
                                    fontSize: 36,
                                    color: midnight.onSurface,
                                    filter: hideFinances ? 'blur(6px)' : 'none',
                                    userSelect: hideFinances ? 'none' : 'auto',
                                }}>
                                {hideFinances ? 'xxxxx' : salesFormatted}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant }}>
                            Entry fees &amp; tiers
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            gridColumn: { xs: 'span 1', md: 'span 1' },
                            bgcolor: midnight.surfaceContainer,
                            borderRadius: '12px',
                            p: 4,
                            minHeight: { md: 200 },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}>
                        <Box>
                            <Typography sx={{ ...editorialLabelSx, fontSize: 12, mb: 0.5 }}>Community votes</Typography>
                            <Typography sx={{ ...editorialHeadlineSx, fontSize: 36, color: midnight.onSurface }}>
                                {formatNumberWithK(voteCount)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            {[0, 1, 2].map(i => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        border: `2px solid ${midnight.surfaceContainer}`,
                                        bgcolor: midnight.surfaceHighest,
                                        ml: i > 0 ? -1 : 0,
                                    }}
                                />
                            ))}
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    border: `2px solid ${midnight.surfaceContainer}`,
                                    bgcolor: midnight.surfaceHighest,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 8,
                                    fontWeight: 700,
                                    ml: -1,
                                }}>
                                +
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {manageHub}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '8fr 4fr' },
                        gap: 6,
                    }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Box sx={{ bgcolor: midnight.surfaceLow, borderRadius: '12px', p: 4 }}>
                            <Typography
                                sx={{
                                    ...editorialHeadlineSx,
                                    fontSize: 24,
                                    mb: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}>
                                <DescriptionOutlinedIcon sx={{ color: midnight.primary }} />
                                Campaign strategy
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
                                <Box sx={{ width: '100%', pl: 0, ml: 0 }}>
                                    <Typography
                                        component="div"
                                        sx={{
                                            ...editorialLabelSx,
                                            letterSpacing: '0.2em',
                                            mb: 1,
                                            pl: 0,
                                            ml: 0,
                                            textAlign: 'left',
                                        }}>
                                        Primary objective
                                    </Typography>
                                    {contest.description?.trim() ? (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                pl: 0,
                                                ml: 0,
                                                ...brandContestRichTextViewerSx,
                                                color: midnight.onSurface,
                                                '& .rich-text-wrapper': { pl: 0, ml: 0 },
                                                '& .rich-text-wrapper .ql-editor': {
                                                    color: `${midnight.onSurface} !important`,
                                                },
                                                '& .rich-text-wrapper .ql-editor > div': {
                                                    padding: 0,
                                                    margin: 0,
                                                },
                                                '& .rich-text-wrapper .ql-editor p': {
                                                    paddingLeft: 0,
                                                    marginLeft: 0,
                                                },
                                            }}>
                                            <RichTextViewer
                                                content={contest.description}
                                                textColor={midnight.onSurface}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography
                                            sx={{ fontWeight: 500, lineHeight: 1.7, color: midnight.onSurface }}>
                                            Add a campaign description in the full manager.
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ width: '100%', pl: 0, ml: 0 }}>
                                    <Typography
                                        component="div"
                                        sx={{
                                            ...editorialLabelSx,
                                            letterSpacing: '0.2em',
                                            mb: 1,
                                            pl: 0,
                                            ml: 0,
                                            textAlign: 'left',
                                        }}>
                                        Brand voice
                                    </Typography>
                                    <Typography sx={{ fontWeight: 500, lineHeight: 1.7, color: midnight.onSurface }}>
                                        {genres || tags
                                            ? [genres && `Genres: ${genres}`, tags && `Tags: ${tags}`]
                                                  .filter(Boolean)
                                                  .join(' · ')
                                            : 'Define genre and tags to shape creator expectations.'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ bgcolor: midnight.surfaceLow, borderRadius: '12px', p: 4 }}>
                            <Typography
                                sx={{
                                    ...editorialHeadlineSx,
                                    fontSize: 24,
                                    mb: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}>
                                <GroupOutlinedIcon sx={{ color: midnight.primary }} />
                                Creator guidelines
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {guidelines.length === 0 ? (
                                    <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 14 }}>
                                        No requirements listed yet.
                                    </Typography>
                                ) : (
                                    guidelines.map((label, idx) => (
                                        <EditorialListRow
                                            key={`${label}-${idx}`}
                                            label={label}
                                            Icon={guidelineIcons[idx % guidelineIcons.length]}
                                            toneIndex={idx}
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box
                            sx={{
                                bgcolor: midnight.surfaceContainer,
                                p: 4,
                                borderRadius: '12px',
                                border: '1px solid rgba(72,72,71,0.1)',
                            }}>
                            <Typography sx={{ ...editorialHeadlineSx, fontSize: 20, mb: 3 }}>Milestones</Typography>
                            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                                {milestones.map((m, idx) => (
                                    <Box
                                        component="li"
                                        key={m.title}
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            mb: idx < milestones.length - 1 ? 3 : 0,
                                            opacity: m.muted ? 0.3 : 1,
                                        }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <m.Icon sx={{ fontSize: 18, color: m.iconColor }} />
                                            {idx < milestones.length - 1 && (
                                                <Box
                                                    sx={{
                                                        width: 1,
                                                        flex: 1,
                                                        minHeight: 24,
                                                        bgcolor: 'rgba(72,72,71,0.3)',
                                                        mt: 0.5,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{m.title}</Typography>
                                            <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant }}>
                                                {m.subtitle}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                bgcolor: midnight.surfaceContainer,
                                p: 4,
                                borderRadius: '12px',
                                border: '1px solid rgba(72,72,71,0.1)',
                            }}>
                            <Typography sx={{ ...editorialHeadlineSx, fontSize: 20, mb: 2 }}>Funding status</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: 8,
                                        bgcolor: midnight.surfaceHighest,
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                        display: 'flex',
                                    }}>
                                    <Box
                                        sx={{ width: `${fundingPct}%`, bgcolor: midnight.secondary, height: '100%' }}
                                    />
                                    <Box
                                        sx={{
                                            width: `${Math.max(0, 100 - fundingPct)}%`,
                                            bgcolor: midnight.primary,
                                            height: '100%',
                                            opacity: contest.funded ? 0.6 : 0.3,
                                        }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        color: midnight.onSurfaceVariant,
                                    }}>
                                    {contest.funded ? 'Funded' : 'Pending'}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, lineHeight: 1.6 }}>
                                {contest.funded
                                    ? prizePoolDisplay
                                        ? `${escrowDisplay} locked in escrow · ${prizePoolDisplay} prize pool configured.`
                                        : `${escrowDisplay} locked in escrow for prize payouts.`
                                    : prizePoolDisplay
                                    ? `${prizePoolDisplay} prize pool required · fund from your campaign wallet before publishing.`
                                    : 'Lock prize funds from your campaign wallet before publishing.'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ bgcolor: midnight.surfaceLow, borderRadius: '12px', p: 4, width: '100%', mt: 6 }}>
                    <Typography
                        sx={{
                            ...editorialHeadlineSx,
                            fontSize: 24,
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}>
                        <BuildOutlinedIcon sx={{ color: midnight.primary }} />
                        Technical specifications
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            flexWrap: { xs: 'nowrap', md: 'nowrap' },
                            gap: 2,
                            width: '100%',
                            justifyContent: { md: 'space-between' },
                            alignItems: 'stretch',
                        }}>
                        {technicalSpecRows.map((row, idx) => (
                            <EditorialSpecTile
                                key={row.label}
                                label={row.label}
                                value={row.value}
                                Icon={row.Icon}
                                toneIndex={idx}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
